import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "adeshwar_customer_token_v1";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=checking, null=guest (active accounts only)
  const [pendingUser, setPendingUser] = useState(null); // logged-in but awaiting approval (Google)
  const [loginOpen, setLoginOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const applyAccount = (u) => {
    if (u?.status === "active") {
      setUser(u);
      setPendingUser(null);
    } else if (u) {
      setUser(null);
      setPendingUser(u);
      if (!u.phone || !u.business_name) setProfileOpen(true);
    } else {
      setUser(null);
      setPendingUser(null);
    }
  };

  useEffect(() => {
    // Emergent Google OAuth callback: process session_id from URL fragment FIRST
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      axios
        .post(`${API}/auth/google/session`, { session_id: sessionId }, { withCredentials: true })
        .then((res) => {
          localStorage.setItem(TOKEN_KEY, res.data.session_token);
          applyAccount(res.data.user);
        })
        .catch(() => setUser(null));
      return;
    }
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setUser(null); return; }
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true })
      .then((res) => applyAccount(res.data))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
      });
  }, []);

  const loginSuccess = (token, u) => {
    localStorage.setItem(TOKEN_KEY, token);
    applyAccount(u);
  };

  const logout = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    axios
      .post(`${API}/auth/logout`, {}, { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true })
      .catch(() => {});
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setPendingUser(null);
  };

  const openLogin = () => setLoginOpen(true);

  const value = useMemo(
    () => ({ user, pendingUser, setPendingUser, loginSuccess, logout, loginOpen, setLoginOpen, openLogin, profileOpen, setProfileOpen }),
    [user, pendingUser, loginOpen, profileOpen] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
